import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { Product, ProductVariant } from "@/services/products";

export type CartItem = {
  /** productId 與 variantId（若有）組成的唯一鍵 */
  key: string;
  productId: number;
  variantId: number | null;
  slug: string;
  name: string;
  image: string;
  sku: string;
  variantLabel: string;
  unitPrice: number;
  originalPrice: number | null;
  quantity: number;
  /** null 表示不限庫存 */
  maxStock: number | null;
};

type AddItemResult = {
  ok: boolean;
  message?: string;
};

type CartState = {
  items: CartItem[];
};

type CartAction =
  | { type: "hydrate"; items: CartItem[] }
  | { type: "add"; item: CartItem }
  | { type: "updateQuantity"; key: string; quantity: number }
  | { type: "remove"; key: string }
  | { type: "clear" };

const STORAGE_KEY = "fulling_cart_items_v1";

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "hydrate":
      return { items: action.items };
    case "add": {
      const existingIndex = state.items.findIndex((item) => item.key === action.item.key);
      if (existingIndex === -1) {
        return { items: [...state.items, action.item] };
      }
      const items = [...state.items];
      const existing = items[existingIndex];
      const nextQuantity = existing.maxStock !== null
        ? Math.min(existing.quantity + action.item.quantity, existing.maxStock)
        : existing.quantity + action.item.quantity;
      items[existingIndex] = { ...existing, quantity: nextQuantity };
      return { items };
    }
    case "updateQuantity": {
      const items = state.items
        .map((item) => {
          if (item.key !== action.key) return item;
          const clamped = item.maxStock !== null ? Math.min(action.quantity, item.maxStock) : action.quantity;
          return { ...item, quantity: Math.max(0, clamped) };
        })
        .filter((item) => item.quantity > 0);
      return { items };
    }
    case "remove":
      return { items: state.items.filter((item) => item.key !== action.key) };
    case "clear":
      return { items: [] };
    default:
      return state;
  }
}

type CartContextValue = {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addToCart: (product: Product, variant: ProductVariant | null, quantity?: number) => AddItemResult;
  updateQuantity: (key: string, quantity: number) => void;
  removeItem: (key: string) => void;
  clearCart: () => void;
  totalQuantity: number;
  subtotal: number;
};

const CartContext = createContext<CartContextValue | null>(null);

function readStoredItems(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  // 使用 useReducer 的 lazy initializer 在「第一次渲染當下」同步讀取 localStorage，
  // 避免舊版「先以空陣列渲染、再用 effect 補讀」的作法在補讀完成前，
  // 讓「寫入 localStorage」的 effect 用尚未補讀的空陣列把已存在的購物車資料覆蓋掉（重新整理後購物車消失的成因）。
  const [state, dispatch] = useReducer(cartReducer, undefined, (): CartState => ({ items: readStoredItems() }));
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
  }, [state.items]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((current) => !current), []);

  const addToCart = useCallback((product: Product, variant: ProductVariant | null, quantity = 1): AddItemResult => {
    if (product.productType !== "Sale") {
      return { ok: false, message: "此商品僅供展示，暫不開放線上購買。" };
    }
    if (product.hasVariants && !variant) {
      return { ok: false, message: "請先選擇規格。" };
    }
    if (variant && !variant.isActive) {
      return { ok: false, message: "此規格目前無法購買。" };
    }

    const maxStock = variant ? variant.stock : product.stock;
    if (maxStock !== null && maxStock <= 0) {
      return { ok: false, message: "此商品目前已無庫存。" };
    }

    const unitPrice = variant ? (variant.salePrice ?? variant.price) : (product.salePrice ?? product.price);
    const originalPrice = variant
      ? (variant.salePrice !== null ? variant.price : null)
      : (product.salePrice !== null ? product.price : null);
    const safeQuantity = maxStock !== null ? Math.min(Math.max(1, quantity), maxStock) : Math.max(1, quantity);

    const item: CartItem = {
      key: variant ? `${product.id}-${variant.id}` : `${product.id}`,
      productId: product.id,
      variantId: variant ? variant.id : null,
      slug: product.slug || String(product.id),
      name: product.name,
      image: variant?.imageUrl || product.image,
      sku: variant ? variant.sku : product.productCode,
      variantLabel: variant ? variant.optionLabel : "",
      unitPrice,
      originalPrice,
      quantity: safeQuantity,
      maxStock,
    };

    dispatch({ type: "add", item });
    return { ok: true };
  }, []);

  const updateQuantity = useCallback((key: string, quantity: number) => {
    dispatch({ type: "updateQuantity", key, quantity });
  }, []);

  const removeItem = useCallback((key: string) => {
    dispatch({ type: "remove", key });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: "clear" });
  }, []);

  const totalQuantity = useMemo(() => state.items.reduce((sum, item) => sum + item.quantity, 0), [state.items]);
  const subtotal = useMemo(() => state.items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0), [state.items]);

  const value = useMemo<CartContextValue>(() => ({
    items: state.items,
    isOpen,
    openCart,
    closeCart,
    toggleCart,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    totalQuantity,
    subtotal,
  }), [state.items, isOpen, openCart, closeCart, toggleCart, addToCart, updateQuantity, removeItem, clearCart, totalQuantity, subtotal]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
